import { Component, computed, inject, signal } from '@angular/core';
import {
  CreateNoteChangePrice,
  NoteChangePrice,
  NoteChangePriceDialogData,
  NoteChangePriceFormField
} from "./types/note-change-price.types";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { BaseModalComponent } from "../../../../../../core/components/modals/base-modal/base-modal.component";
import { noteChangePriceModalResourceType } from './configs/note-change-price-config';
import { RecordUpdateRequestParams } from "../../../../../../core/types/records-table.types";
import { NoteChangePriceService } from "./services/note-change-price.service";
import { MatButton } from "@angular/material/button";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import {
  GetRecordPropertyDataByIdParams
} from "../../../../../../core/components/modals/base-modal/types/base-modal.types";
import { defaultNoteChangePrice } from "./configs/default-note-change-price";
import { noteChangePriceCreateFields, noteChangePriceEditFields } from "./configs/note-change-price-fields";
import {
  NoteChangePriceHeaderComponent
} from "./components/note-change-price-header/note-change-price-header.component";
import { NoteChangePriceFormComponent } from "./components/note-change-price-form/note-change-price-form.component";

@Component({
  selector: 'app-note-change-price-modal',
  imports: [
    BaseModalComponent,
    MatButton,
    ReactiveFormsModule,
    NoteChangePriceHeaderComponent,
    NoteChangePriceFormComponent,
  ],
  templateUrl: './note-change-price-modal.component.html',
  styleUrl: './note-change-price-modal.component.scss'
})
export class NoteChangePriceModalComponent extends BaseModalComponent {
  private noteChangePriceDialogData = inject<NoteChangePriceDialogData>(MAT_DIALOG_DATA);
  private noteChangePriceService: NoteChangePriceService = inject(NoteChangePriceService);

  public readonly noteChangePriceModalResourceType = noteChangePriceModalResourceType;
  public noteChangePriceFormGroup: FormGroup = new FormGroup({});
  public isValid = signal<boolean>(false);
  public noteChangePriceData = signal<NoteChangePrice>(defaultNoteChangePrice);
  public buttonData = computed(() => this.noteChangePriceService.getButtonData(this.noteChangePriceDialogData));

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(noteChangePriceModalResourceType.key);
  }

  public async handleCreate(): Promise<void> {
    const createNoteChangePrice: CreateNoteChangePrice = {
      noteId: Number(this.noteChangePriceDialogData.parentData.id),
      ...this.noteChangePriceFormGroup.value
    };

    await this.noteChangePriceService.createNoteChangePrice(createNoteChangePrice)
      .then((data) => this.handleCreateSuccess(data));
  }

  public async handleUpdateField(updateRequestParams: RecordUpdateRequestParams): Promise<void> {
    await this.updatedRecordField<NoteChangePrice>(updateRequestParams)
      .then((data) => this.successLoadData(data));
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(noteChangePriceModalResourceType.key);
    this.isValid.set(!!this.buttonData().id);
    this.initForm();
    this.isValid() && await this.fetchNoteChangePriceData();
  }

  private initForm(): void {
    const fields = this.isValid() ? noteChangePriceEditFields : noteChangePriceCreateFields;
    this.noteChangePriceFormGroup = this.baseModalService.getFormGroup<NoteChangePriceFormField>(fields);
  }

  private async fetchNoteChangePriceData(): Promise<void> {
    const params: GetRecordPropertyDataByIdParams = {
      path: noteChangePriceModalResourceType.path,
      resourceId: Number(this.buttonData().id)
    };

    await this.fetchRecordPropertyDataById<NoteChangePrice>(params)
      .then((data) => this.successLoadData(data));
  }

  private successLoadData(data: NoteChangePrice): void {
    this.noteChangePriceData.set(data);
    this.noteChangePriceFormGroup.patchValue(this.noteChangePriceData());
  }

  private handleCreateSuccess(data: NoteChangePrice): void {
    this.isValid.set(true);
    this.initForm();
    this.noteChangePriceData.set(data);
    this.noteChangePriceFormGroup.patchValue(data);
  }
}
